import React, {useRef, useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {Calculator} from 'lucide-react';

import {getLabel} from '../copilot/util';
import FootingCalculationDialog from '../copilot/FootingCalculationDialog';
import DismissSuggestionButton, {
  FOOTING_DISMISS_REASONS,
} from './DismissSuggestionButton';

const styles = {
  card: {
    unselected: {
      border: '1px solid transparent',
      borderRadius: '8px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#F2F5F4',
      },
    },
    selected: {
      borderRadius: '8px',
      border: '1px solid #DA3E30',
      boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.15)',
    },
    pill: {
      backgroundColor: '#FFE5E2',
      color: '#962419',
      fontWeight: 500,
      fontSize: '11px',
      borderRadius: '8px',
      padding: '2px 8px',
      width: 'min-content',
      whiteSpace: 'nowrap',
    },
    pillSuccess: {
      backgroundColor: '#EDFAD8',
      color: '#4A7C1E',
      fontWeight: 500,
      fontSize: '11px',
      borderRadius: '8px',
      padding: '2px 8px',
      width: 'min-content',
      whiteSpace: 'nowrap',
    },
    label: {
      fontSize: '15px',
      fontWeight: 500,
      color: '#162523',
    },
    labelSelected: {
      fontSize: '15px',
      fontWeight: 600,
      color: '#162523',
    },
    valueLabel: {
      color: '#043B34',
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: '160%',
    },
    pageLabel: {
      color: '#67716E',
      fontSize: '12px',
      fontWeight: 400,
    },
    calculatorButton: {
      borderRadius: '4px',
      padding: '4px',
      '&:hover': {
        backgroundColor: '#E2F0EA',
        '& svg': {
          color: '#26A993 !important',
          stroke: '#26A993 !important',
        },
      },
    },
  },
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
};

const PDFFootingCard = ({
  footing,
  isSelected = false,
  onSelect,
  documentId,
  onDismiss,
  onDismissError,
}) => {
  const cardRef = useRef(null);
  const isError = !footing.is_footing_correct;
  const [isCalculationOpen, setIsCalculationOpen] = useState(false);

  const hasCalculationData =
    footing.calculation_expression &&
    footing.value_expression &&
    footing.table_id;

  const handleCardClick = useCallback(() => {
    if (!isSelected) {
      onSelect?.(footing);
    }
  }, [isSelected, footing, onSelect]);

  // Collapsed card view
  if (!isSelected) {
    return (
      <Box ref={cardRef}>
        <Divider sx={{borderColor: '#DCE2E0'}} />
        <Stack
          direction="column"
          py={2}
          px={1}
          onClick={handleCardClick}
          sx={styles.card.unselected}
        >
          <Stack direction="column" gap={1}>
            <Typography
              sx={isError ? styles.card.pill : styles.card.pillSuccess}
            >
              {isError ? 'Footing error' : 'Footing correct'}
            </Typography>
            <Typography sx={styles.card.label}>{getLabel(footing)}</Typography>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // Expanded card view
  return (
    <Box ref={cardRef}>
      <Stack direction="column" sx={styles.card.selected}>
        <Stack gap={1} p={2}>
          <Typography sx={isError ? styles.card.pill : styles.card.pillSuccess}>
            {isError ? 'Footing error' : 'Footing correct'}
          </Typography>
          <Typography sx={styles.card.labelSelected}>
            {getLabel(footing)}
          </Typography>
        </Stack>

        <Divider sx={{borderColor: '#E1E5E4'}} />

        {/* Value details */}
        <Stack
          px={2}
          py={1.5}
          sx={{backgroundColor: '#F2F5F4', borderRadius: '8px'}}
        >
          <Typography sx={styles.card.valueLabel}>
            Recalculated amount = {formatValue(footing.calculated_value)}
          </Typography>
        </Stack>

        <Divider sx={{borderColor: '#E1E5E4'}} />

        <Stack
          direction="row"
          paddingY={1}
          paddingX={2}
          justifyContent="space-between"
        >
          <DismissSuggestionButton
            documentId={documentId}
            discrepancyId={footing.id}
            checkType="footings"
            reasons={FOOTING_DISMISS_REASONS}
            headerText="Dismiss footing error? Tell us why:"
            errorMessage="Failed to dismiss footing error. Please try again."
            popoverId="pdf-footing-dismiss-popover"
            onDismiss={onDismiss}
            onDismissError={onDismissError}
          />
          {hasCalculationData && (
            <Tooltip title="Show calculation" placement="top" enterDelay={500}>
              <IconButton
                onClick={() => setIsCalculationOpen(true)}
                sx={styles.card.calculatorButton}
              >
                <Calculator size={20} color="#0F1E24" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {hasCalculationData && isCalculationOpen && (
        <FootingCalculationDialog
          open={isCalculationOpen}
          handleClose={() => setIsCalculationOpen(false)}
          tableId={footing.table_id}
          cellExpression={footing.calculation_expression}
          valueExpression={footing.value_expression}
          calculatedValue={String(footing.calculated_value ?? '')}
          isPdfReview
        />
      )}
    </Box>
  );
};

PDFFootingCard.propTypes = {
  footing: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_footing_correct: PropTypes.bool,
    presented_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    calculated_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    page_number: PropTypes.number,
    table_page_number: PropTypes.number,
    table_id: PropTypes.string,
    calculation_expression: PropTypes.string,
    value_expression: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  documentId: PropTypes.string,
  onDismiss: PropTypes.func,
  onDismissError: PropTypes.func,
};

export default PDFFootingCard;
